import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Maximize2, Pencil, RotateCcw, RotateCw, Trash2, X } from 'lucide-react';
import {
  useDeleteSubmission,
  useRejectSubmission,
  useSubmission,
  useSubmissionImages,
  useSubmissions,
  useUpdateSubmission,
} from '@/hooks/useSubmissions';
import { useCompetition, useCompetitionContext } from '@/hooks/useCompetitions';
import { useMyScores, useSubmissionScores } from '@/hooks/useJudging';
import { PageSpinner, Spinner } from '@/components/ui/Spinner';
import { Alert, ErrorState, EmptyState } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/store/authStore';
import { JudgeScoreForm } from '@/components/submissions/JudgeScoreForm';
import { JudgeQueueFinish } from '@/components/submissions/JudgeQueueFinish';
import { RejectSubmissionModal } from '@/components/submissions/RejectSubmissionModal';
import { RotatablePhoto, type Rotation, turn } from '@/components/submissions/RotatablePhoto';
import { imageStatusLabel, isImageProcessingDone } from '@/lib/images';
import { CompetitionStatus, SubmissionStatus } from '@/api/types';
import { submissionStatusLabel, submissionStatusTone } from '@/lib/submissions';

export function SubmissionDetailsPage() {
  const { submissionId = '' } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // The list the judge opened this entry from (set by the queue page), so "back"
  // returns to the same filter and moving between entries keeps it.
  const routeState = location.state as { backTo?: string } | null;
  const user = useAuthStore((s) => s.user);
  const submission = useSubmission(submissionId);
  const images = useSubmissionImages(submissionId);
  // Scoped to the submission's OWN competition - a user who judges elsewhere
  // or manages a different organization shouldn't see the scoring/moderation
  // panel here just because they hold that role somewhere on the platform.
  const context = useCompetitionContext(submission.data?.competitionId);
  const competition = useCompetition(submission.data?.competitionId);
  const canSeeScores = (context.data?.isJudge || context.data?.canManage) ?? false;
  const canModerate = context.data?.canManage ?? false;
  // Mirrors JudgingService.SubmitScoreAsync - scores can only be submitted or
  // edited while the competition is in its own judging phase, not before
  // (still accepting entries) or after (already finished).
  const canEditScore = competition.data?.status === CompetitionStatus.Judging;
  const isJudgeHere = context.data?.isJudge ?? false;
  const scores = useSubmissionScores(canSeeScores ? submissionId : undefined);
  // The calling judge's own scores in this competition - tells us which queue
  // entries are still unscored (managers have no scores of their own).
  const myScores = useMyScores(isJudgeHere ? submission.data?.competitionId : undefined);
  // True once the judge has saved the last entry of the queue - shows the
  // "end of category" panel instead of the score form.
  const [finished, setFinished] = useState(false);
  const myExistingScore = scores.data?.items.find((score) => score.judgeUserId === user?.id) ?? null;
  const rejectSubmission = useRejectSubmission();
  const updateSubmission = useUpdateSubmission(submissionId);
  const deleteSubmission = useDeleteSubmission();
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Viewing aid only - never persisted. Reset per entry below.
  const [rotation, setRotation] = useState<Rotation>(0);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  const rotate = useCallback((dir: 1 | -1) => setRotation((r) => turn(r, dir)), []);

  // Judging queue - scoped to the submission's OWN category, never the whole
  // competition. A judge's sense of scale only stays consistent within one
  // category, so "next" must never jump into a different category mid-session
  // (see JudgeScoringPage, which requires picking a category up front).
  const categoryId = submission.data?.categoryId;
  const queue = useSubmissions({ categoryId }, { enabled: Boolean(categoryId) && canSeeScores });
  const queueItems = queue.data?.items;
  const queueIndex = queueItems?.findIndex((item) => item.id === submissionId) ?? -1;
  const nextSubmission = queueItems && queueIndex >= 0 ? queueItems[queueIndex + 1] : undefined;

  const prevSubmission = queueItems && queueIndex > 0 ? queueItems[queueIndex - 1] : undefined;

  const nextSubmissionId = nextSubmission?.id;
  const prevSubmissionId = prevSubmission?.id;
  const goToNext = useCallback(() => {
    if (nextSubmissionId) navigate(`/submissions/${nextSubmissionId}`, { state: routeState });
  }, [navigate, nextSubmissionId, routeState]);
  const goToPrev = useCallback(() => {
    if (prevSubmissionId) navigate(`/submissions/${prevSubmissionId}`, { state: routeState });
  }, [navigate, prevSubmissionId, routeState]);
  const goToId = useCallback((id: string) => navigate(`/submissions/${id}`, { state: routeState }), [navigate, routeState]);

  // Which entries of this category the judge has already scored. While the
  // finish panel is up the entry just saved counts as scored even if the
  // refreshed score list has not arrived yet.
  const scoredIds = new Set((myScores.data?.items ?? []).map((sc) => sc.submissionId));
  if (finished) scoredIds.add(submissionId);
  const unscored = queueItems?.filter((item) => !scoredIds.has(item.id)) ?? [];
  // Next unscored entry after this one, wrapping around to the start.
  const nextUnscoredId =
    queueItems && queueIndex >= 0
      ? [...queueItems.slice(queueIndex + 1), ...queueItems.slice(0, queueIndex)].find((item) => !scoredIds.has(item.id))?.id
      : undefined;
  const isLastInQueue = Boolean(queueItems) && queueIndex >= 0 && queueIndex === (queueItems?.length ?? 0) - 1;
  const showJudgeProgress = isJudgeHere && myScores.isSuccess;

  useEffect(() => {
    setRotation(0);
    setFinished(false);
  }, [submissionId]);

  // Esc closes fullscreen; R / Shift+R rotate. Score keys live in JudgeScoreForm.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
        return;
      }
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      if (t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      if (e.key === 'r') rotate(1);
      else if (e.key === 'R') rotate(-1);
      else if (e.key === 'ArrowRight') goToNext();
      else if (e.key === 'ArrowLeft') goToPrev();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rotate, goToNext, goToPrev]);

  // Lock page scroll behind the fullscreen stage.
  useEffect(() => {
    if (!isFullscreen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isFullscreen]);

  if (submission.isLoading || context.isLoading || competition.isLoading) return <PageSpinner />;
  if (submission.isError) {
    return <ErrorState message={getApiErrorMessage(submission.error)} onRetry={() => submission.refetch()} />;
  }
  if (!submission.data) return <EmptyState title="Prispevek ni najden" />;

  const s = submission.data;
  const image = images.data?.items[0];
  // Mirrors Submission.UpdateDetails' own status gate (Draft/Submitted, not
  // yet Accepted/Rejected) and ImagesService.DeleteAsync's identical rule -
  // once an organizer has decided, the competitor can no longer rename the
  // entry or pull its image out from under that decision.
  const isOwner = Boolean(user?.id) && user?.id === s.competitorUserId;
  const canEdit = isOwner && (s.status === SubmissionStatus.Draft || s.status === SubmissionStatus.Submitted);

  function startEditingTitle() {
    setTitleDraft(s.title);
    setIsEditingTitle(true);
  }

  async function saveTitle() {
    if (!titleDraft.trim()) return;
    try {
      await updateSubmission.mutateAsync({ title: titleDraft, description: s.description });
      setIsEditingTitle(false);
    } catch {
      // Error surfaced inline below via updateSubmission.error - stay in edit mode.
    }
  }

  function handleDeleteSubmission() {
    if (!confirm(`Izbrišem prispevek "${s.title}" skupaj s sliko? Tega dejanja ni mogoče razveljaviti.`)) return;
    deleteSubmission.mutate(submissionId, {
      onSuccess: () => navigate(`/competitions/${competition.data?.slug ?? s.competitionId}`, { replace: true }),
    });
  }
  // Judges/organizers need the true, unwatermarked image to review it
  // properly - the tiled watermark text obscures exactly the detail judging
  // requires. Everyone else gets the watermarked version (the protected,
  // publicly-shareable one), same as before. The thumbnail is only a
  // last-resort fallback while the fuller sizes haven't been generated yet.
  const displayUrl = canSeeScores
    ? (image?.previewUrl ?? image?.watermarkedUrl ?? image?.thumbnailUrl)
    : (image?.watermarkedUrl ?? image?.previewUrl ?? image?.thumbnailUrl);

  const stageButtonClass =
    'rounded-control border border-line bg-surface-1/80 p-2 text-fg backdrop-blur-sm transition-colors duration-fast hover:bg-surface-2';

  const rotateButtons = displayUrl && (
    <>
      <button type="button" onClick={() => rotate(-1)} title="Zavrti v levo (Shift+R)" className={stageButtonClass}>
        <RotateCcw className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only">Zavrti v levo</span>
      </button>
      <button type="button" onClick={() => rotate(1)} title="Zavrti v desno (R)" className={stageButtonClass}>
        <RotateCw className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only">Zavrti v desno</span>
      </button>
    </>
  );

  const queueCounter = canSeeScores && queueItems && queueItems.length > 0 && queueIndex >= 0 && (
    <div className="flex flex-wrap items-center gap-2">
      <span className="rounded-control border border-line bg-surface-1/80 px-2.5 py-1.5 font-mono text-[11px] tabular-nums text-fg backdrop-blur-sm">
        Slika {queueIndex + 1} od {queueItems.length} v kategoriji
        {showJudgeProgress && ` · neocenjenih: ${unscored.length}`}
      </span>
      {showJudgeProgress && nextUnscoredId && (
        <button
          type="button"
          onClick={() => goToId(nextUnscoredId)}
          className="rounded-control border border-line bg-surface-1/80 px-2.5 py-1.5 text-[11px] font-medium text-fg backdrop-blur-sm transition-colors duration-fast hover:bg-surface-2"
        >
          Naslednja neocenjena
        </button>
      )}
    </div>
  );

  // Big previous/next arrows on the sides of the photo (also ← / →).
  const navArrowClass =
    'absolute top-1/2 -translate-y-1/2 rounded-full border border-line bg-surface-1/80 p-2.5 text-fg backdrop-blur-sm transition-colors duration-fast hover:bg-surface-2';
  const navArrows = canSeeScores && queueItems && queueItems.length > 1 && (
    <>
      {prevSubmission && (
        <button type="button" onClick={goToPrev} title="Prejšnja slika (←)" className={`${navArrowClass} left-3`}>
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
          <span className="sr-only">Prejšnja slika</span>
        </button>
      )}
      {nextSubmission && (
        <button type="button" onClick={goToNext} title="Naslednja slika (→)" className={`${navArrowClass} right-3`}>
          <ChevronRight className="h-6 w-6" aria-hidden="true" />
          <span className="sr-only">Naslednja slika</span>
        </button>
      )}
    </>
  );

  // Where "back" leads: a judge returns to the same category in their queue,
  // an organizer to the competition page.
  const backHref =
    routeState?.backTo ??
    (isJudgeHere
      ? `/judge/scoring?competition=${s.competitionId}&category=${s.categoryId}`
      : `/competitions/${competition.data?.slug ?? s.competitionId}`);
  const backLabel = isJudgeHere ? 'Nazaj na čakalno vrsto' : 'Nazaj na tekmovanje';

  const scoreForm = canSeeScores && (
    <JudgeScoreForm
      key={submissionId}
      submissionId={submissionId}
      existingScore={myExistingScore}
      canEdit={canEditScore}
      hasNext={Boolean(nextSubmission)}
      onNext={goToNext}
      hasPrev={Boolean(prevSubmission)}
      onPrev={goToPrev}
      onFinish={isLastInQueue && isJudgeHere ? () => setFinished(true) : undefined}
      layout={isFullscreen ? 'panel' : 'inline'}
    />
  );

  const scoringArea =
    finished && queueItems ? (
      <JudgeQueueFinish
        total={queueItems.length}
        unscoredCount={unscored.length}
        backHref={backHref}
        onGoToFirstUnscored={unscored[0] ? () => goToId(unscored[0].id) : undefined}
        onRestart={() => goToId(queueItems[0].id)}
        onBackToLast={() => setFinished(false)}
      />
    ) : (
      scoreForm
    );

  return (
    <div className="flex flex-col gap-3">
      {canSeeScores && (
        <Link
          to={backHref}
          className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-muted transition-colors duration-fast hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {backLabel}
        </Link>
      )}
      {/*
        Fixed height budgeted from the viewport (minus the topbar and page
        padding) rather than a guessed vh fraction on the image alone - the
        image sits in a flex-1/min-h-0 area that fills whatever room is left
        after the title and score sections take their own natural height, so
        it's as big as possible AND the score buttons never require scrolling,
        on any viewport (a fixed vh number can't satisfy both at once: too big
        and the buttons fall off-screen, too small and the image is cramped).
        A short min-height floor lets the whole thing scroll normally on
        small/mobile viewports instead of squashing everything unusably thin.
      */}
      <Card className="flex h-[calc(100vh-9rem)] min-h-[420px] flex-col overflow-hidden">
        {image?.requirementIssues && (
          <div className="border-b border-line p-3">
            <Alert variant="warning">
              Slika ne ustreza zahtevam tekmovanja: {image.requirementIssues}
              {canEdit
                ? ' Nadomestite jo z ustrezno sliko, sicer jo lahko organizator zavrne.'
                : ' Organizator lahko prispevek zavrne.'}
            </Alert>
          </div>
        )}
        {/* Photo stage: the one place that uses the dark token set (UI_GUIDELINES §1, §6). */}
        <div data-theme="dark" className="relative min-h-0 flex-1 bg-surface-0 text-fg">
          {displayUrl ? (
            <RotatablePhoto
              src={displayUrl}
              alt={s.title}
              rotation={rotation}
              onClick={() => setIsFullscreen(true)}
              imgClassName="cursor-zoom-in"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-1 text-sm text-fg-muted">
              {images.isLoading && <span>Nalaganje stanja slike&hellip;</span>}
              {images.isError && <span>Stanja slike ni bilo mogoče naložiti.</span>}
              {images.isSuccess && !image && <span>Slika še ni bila naložena.</span>}
              {image && (
                <>
                  <span className="font-medium text-fg-secondary">{image.originalFileName}</span>
                  <span>{imageStatusLabel(image.status)}</span>
                  {!isImageProcessingDone(image.status) && (
                    <span className="mt-2 flex flex-col items-center gap-2 text-xs">
                      <Spinner />
                      Slika se obdeluje, predogled se prikaže sam.
                    </span>
                  )}
                </>
              )}
            </div>
          )}
          {(displayUrl || canEdit) && (
            <div className="absolute right-3 top-3 flex items-center gap-2">
              {rotateButtons}
              {canEdit && (
                <button
                  type="button"
                  onClick={handleDeleteSubmission}
                  disabled={deleteSubmission.isPending}
                  title="Izbriši prispevek"
                  aria-label="Izbriši prispevek"
                  className="rounded-control border border-line bg-surface-1/80 p-2 text-fg backdrop-blur-sm transition-colors duration-fast hover:bg-danger-subtle hover:text-danger disabled:cursor-not-allowed disabled:text-fg-muted"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
              {displayUrl && (
                <button
                  type="button"
                  onClick={() => setIsFullscreen(true)}
                  title="Celozaslonski prikaz"
                  className={stageButtonClass}
                >
                  <Maximize2 className="h-5 w-5" aria-hidden="true" />
                  <span className="sr-only">Celozaslonski prikaz</span>
                </button>
              )}
            </div>
          )}
          {navArrows}
          {queueCounter && <div className="absolute left-3 top-3">{queueCounter}</div>}
        </div>
        <CardBody className="shrink-0 py-3">
          {isEditingTitle ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <Input
                  aria-label="Naslov prispevka"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  autoFocus
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  isLoading={updateSubmission.isPending}
                  onClick={saveTitle}
                  aria-label="Shrani ime"
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditingTitle(false)}
                  aria-label="Prekliči"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {updateSubmission.isError && (
                <span className="text-xs text-danger">{getApiErrorMessage(updateSubmission.error)}</span>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <h1 className="text-lg font-semibold text-fg">{s.title}</h1>
                {canEdit && (
                  <button
                    type="button"
                    onClick={startEditingTitle}
                    aria-label="Preimenuj prispevek"
                    className="rounded-control p-1 text-fg-muted transition-colors duration-fast hover:bg-surface-2 hover:text-fg"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Badge tone={submissionStatusTone(s.status)}>{submissionStatusLabel(s.status)}</Badge>
            </div>
          )}
        </CardBody>

        {/* Same card as the image, directly below the title, with no gap or
            card boundary in between - a judge never has to scroll or look
            elsewhere to find the score buttons. */}
        {canSeeScores && !isFullscreen && (
          <CardBody className="shrink-0 border-t border-line py-3">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h2 className="font-semibold text-fg">Oceni ta prispevek</h2>
              {competition.data?.judgingDeadline && canEditScore && (
                <p className="text-xs text-fg-muted">
                  Rok za ocenjevanje: {new Date(competition.data.judgingDeadline).toLocaleString()}
                </p>
              )}
            </div>
            {scoringArea}
          </CardBody>
        )}
      </Card>

      <Card>
        <CardBody>
          {deleteSubmission.isError && <Alert variant="error">{getApiErrorMessage(deleteSubmission.error)}</Alert>}
          {s.description && <p className="text-sm text-fg-secondary">{s.description}</p>}
          <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
            {s.rejectionReason && (
              <>
                <dt className="text-fg-muted">Razlog za zavrnitev</dt>
                <dd className="font-medium text-fg">{s.rejectionReason}</dd>
              </>
            )}
            {s.createdAt && (
              <>
                <dt className="text-fg-muted">Oddano</dt>
                <dd className="font-medium text-fg">{new Date(s.createdAt).toLocaleString()}</dd>
              </>
            )}
          </dl>
          {canModerate &&
            (s.status === SubmissionStatus.Submitted || s.status === SubmissionStatus.Accepted) &&
            (competition.data?.status === CompetitionStatus.Active || competition.data?.status === CompetitionStatus.Review) && (
              <div className="mt-4 flex gap-2 border-t border-line pt-4">
                <Button
                  variant="danger"
                  onClick={() => {
                    rejectSubmission.reset();
                    setIsRejecting(true);
                  }}
                >
                  Zavrni fotografijo
                </Button>
              </div>
            )}
          {isRejecting && (
            <RejectSubmissionModal
              title={s.title}
              initialReason={image?.requirementIssues ?? ''}
              isLoading={rejectSubmission.isPending}
              error={rejectSubmission.isError ? getApiErrorMessage(rejectSubmission.error) : undefined}
              onClose={() => setIsRejecting(false)}
              onConfirm={(reason) => rejectSubmission.mutate({ id: s.id, reason }, { onSuccess: () => setIsRejecting(false) })}
            />
          )}
        </CardBody>
      </Card>

      {canSeeScores && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-fg">Ocene in komentarji</h2>
          </CardHeader>
          <CardBody>
            {scores.isLoading && <PageSpinner />}
            {scores.isError && <ErrorState message={getApiErrorMessage(scores.error)} />}
            {scores.isSuccess && scores.data.items.length === 0 && (
              <p className="text-sm text-fg-muted">Še ni oddanih ocen.</p>
            )}
            {scores.isSuccess && scores.data.items.length > 0 && (
              <ul className="flex flex-col divide-y divide-line">
                {scores.data.items.map((score) => (
                  <li key={score.id} className="py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-fg">Ocena: {score.value}</span>
                    </div>
                    {score.comments && <p className="mt-1 text-sm text-fg-secondary">{score.comments}</p>}
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      )}

      {/*
        Fullscreen judging room: the photo stage (dark) takes all remaining
        space, the scoring panel sits beside it on wide screens and becomes a
        bottom sheet on narrow ones, so a judge can rotate, score and move to
        the next entry without ever leaving fullscreen (UI_GUIDELINES §4, §5).
      */}
      {isFullscreen && displayUrl && (
        <div role="dialog" aria-modal="true" aria-label={s.title} className="fixed inset-0 z-50 flex flex-col bg-surface-0 lg:flex-row">
          <div data-theme="dark" className="relative min-h-0 flex-1 bg-surface-0 text-fg">
            <RotatablePhoto src={displayUrl} alt={s.title} rotation={rotation} />
            {navArrows}
            {queueCounter && <div className="absolute left-3 top-3">{queueCounter}</div>}
            <div className="absolute right-3 top-3 flex items-center gap-2">
              {rotateButtons}
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                title="Zapri celozaslonski prikaz (Esc)"
                className={stageButtonClass}
              >
                <X className="h-5 w-5" aria-hidden="true" />
                <span className="sr-only">Zapri celozaslonski prikaz</span>
              </button>
            </div>
          </div>
          {canSeeScores && (
            <aside
              aria-label="Ocenjevanje"
              className="flex max-h-[50vh] shrink-0 flex-col gap-3 overflow-y-auto border-t border-line bg-surface-1 p-4 text-fg lg:max-h-none lg:w-[380px] lg:border-l lg:border-t-0 lg:p-5"
            >
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-lg font-semibold text-fg">{s.title}</h2>
                <Badge tone={submissionStatusTone(s.status)}>{submissionStatusLabel(s.status)}</Badge>
              </div>
              {competition.data?.judgingDeadline && canEditScore && (
                <p className="text-xs text-fg-muted">
                  Rok za ocenjevanje: {new Date(competition.data.judgingDeadline).toLocaleString()}
                </p>
              )}
              {scoringArea}
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
